import { CForm, CFormGroup, CInputCheckbox, CLabel } from "@coreui/react";
import { signInWithEmailAndPassword } from "@firebase/auth";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  BasicInput,
  ButtonBasic,
  ErrorText,
  LoadingSpinner,
} from "src/components";
import { useHttp } from "src/hooks";
import { checkIsFormTouched } from "src/utils/common-utils";
import { emailRegex } from "src/utils/common-utils/regex";
import { useAuth } from "reactfire";

const SignupForm = ({ track }) => {
  const { t } = useTranslation();
  const [signupError, setSignupError] = useState(null);

  const [hasAccepted, setHasAccepted] = useState(false);
  const auth = useAuth();

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { touchedFields, errors },
  } = useForm();

  const [loading, setLoading] = useState(false);

  const { sendRequest } = useHttp();

  const history = useHistory();
  const onSubmit = async (values) => {
    if (!hasAccepted) return;
    setLoading(true);

    const orgToSave = { ...values, track };

    try {
      const { email, password } = values;

      await sendRequest(
        "/app/users/esg",
        {
          method: "POST",
          body: JSON.stringify(orgToSave),
          successMsg: "User created successfully",
        },
        true
      );

      await signInWithEmailAndPassword(auth, email, password);

      history.push("/survey/account/onboarding");
    } catch (err) {
      setSignupError(err?.message);
      console.log(err.status);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    setValue(e.target.name, e.target.value, { shouldTouch: true });
  };

  register("firstName", {
    setValueAs: (v) => v.trim(),
    required: t("validation.enterName"),
  });
  register("lastName", {
    setValueAs: (v) => v.trim(),
    required: t("validation.enterName"),
  });
  register("email", {
    setValueAs: (v) => v.trim(),
    required: t("validation.enterEmail"),
    pattern: {
      value: emailRegex,
      message: t("validation.enterEmail", "Please enter valid email"),
    },
  });
  register("orgName", {
    setValueAs: (v) => v.trim(),
    required: t("validation.enterOrg", "Please enter organisation name"),
  });
  register("password", {
    setValueAs: (v) => v.trim(),
    minLength: {
      value: 6,
      message: t(
        "validation.passwordLength",
        "Password has to have at least 6 characters"
      ),
    },
    validate: {
      notEmpty: (v) =>
        !!v || t("validation.passwordEmpty", "Password cannot be empty"),
      passwordMatches: (v) =>
        v === getValues("password-confirm") ||
        t("validation.passwordMatch", "Passwords have to match"),
    },
  });
  register("password-confirm", { setValueAs: (v) => v.trim() });

  // const [hasAccepted, setHasAccepted] = useState(!isFreeSignup);

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <CForm onSubmit={handleSubmit(onSubmit)}>
          {signupError && <h5 className="text-danger">* {signupError}</h5>}
          <CFormGroup>
            <CLabel>{t("shared.organisation", "Organisation")}</CLabel>
            <BasicInput
              type="text"
              name="orgName"
              placeholder={t(
                "signup.enterOrg",
                "Please enter the name of organisation..."
              )}
              onChange={onChange}
            />
            <ErrorText value={errors.organisation} />
          </CFormGroup>
          <CFormGroup>
            <CLabel>{t("shared.email")}</CLabel>
            <BasicInput
              type="email"
              name="email"
              placeholder={t(
                "signup.adminEmail",
                "Please enter admin's email..."
              )}
              onChange={onChange}
            />
            <ErrorText value={errors.email} />
          </CFormGroup>
          <CFormGroup>
            <CLabel>{t("shared.firstName")}</CLabel>
            <BasicInput
              type="text"
              name="firstName"
              placeholder={t(
                "signup.adminFirstName",
                "Please enter admin's first name..."
              )}
              onChange={onChange}
            />
            <ErrorText value={errors.firstName} />
          </CFormGroup>
          <CFormGroup>
            <CLabel>{t("shared.lastName")}</CLabel>
            <BasicInput
              type="text"
              name="lastName"
              placeholder={t(
                "signup.adminLastName",
                "Please enter admin's last name..."
              )}
              onChange={onChange}
            />
            <ErrorText value={errors.lastName} />
          </CFormGroup>

          <CFormGroup>
            <CLabel>{t("shared.password")}</CLabel>
            <BasicInput
              type="password"
              name="password"
              placeholder={t("shared.enterPassword", "Please enter password")}
              onChange={onChange}
              autoComplete="new-password"
            />
            <ErrorText value={errors.password} />
          </CFormGroup>
          <CFormGroup>
            <CLabel>{t("assign.repeatPassword", "Repeat password")}</CLabel>
            <BasicInput
              type="password"
              name="password-confirm"
              placeholder={t(
                "assign.repeatPasswordPlaceholder",
                "Please repeat password"
              )}
              autoComplete="new-password"
              onChange={onChange}
            />
          </CFormGroup>

          <CFormGroup className="mt-4 " variant="custom-checkbox">
            <CInputCheckbox
              custom
              id="terms-checkbox"
              onChange={(e) => setHasAccepted(e.target.checked)}
              defaultChecked={false}
              style={{ borderRadius: 0 }}
            />
            <CLabel
              style={{ fontSize: "16px" }}
              variant="custom-checkbox"
              htmlFor="terms-checkbox"
            >
              I accept the
              <a
                className="text-info mx-auto"
                href="https://www.zerobees.com/legal"
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                terms and conditions
              </a>{" "}
              <span className="text-danger ">*</span>
            </CLabel>
          </CFormGroup>
          <CFormGroup className="mt-2 mb-2 " variant="custom-checkbox">
            <CInputCheckbox
              custom
              id="marketing-checkbox"
              onChange={(e) => setValue("acceptsMarketing", e.target.checked)}
              defaultChecked={false}
              style={{ borderRadius: 0 }}
            />
            <CLabel
              style={{ fontSize: "16px" }}
              variant="custom-checkbox"
              htmlFor="marketing-checkbox"
            >
              I want to receive marketing communications from ZeroBees
            </CLabel>
          </CFormGroup>
          <div className="d-flex justify-content-center w-100">
            <ButtonBasic
              size="lg"
              className="mt-3"
              type="submit"
              bgcolor="green"
              hovercolor="darkgreen"
              disabled={!checkIsFormTouched(touchedFields) || !hasAccepted}
            >
              {t("signup.title", "Sign up")}
            </ButtonBasic>
          </div>
        </CForm>
      )}
    </>
  );
};

export default SignupForm;
